import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { LoadingScreen } from '@/components/layout/LoadingScreen';
import {
  isCurrentVersionAccepted,
  useCurrentEulaAcceptance,
} from '@/features/eula/hooks';

/**
 * Gate that ensures the signed-in user has accepted the CURRENT_EULA_VERSION
 * before they can interact with the app shell. Sits inside RequireAuth so it
 * only runs once we know the user has a valid profile.
 *
 * If the user has no acceptance row, or their most recent acceptance row
 * doesn't match the current version (e.g. we bumped the EULA), they get
 * pushed to /legal/eula/accept with their original destination preserved
 * in router state so we can return them to it after they accept.
 */
export function RequireEulaAcceptance({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  const { data, isLoading, isError } = useCurrentEulaAcceptance(user?.id);

  if (isLoading) return <LoadingScreen />;

  // If the lookup itself failed (network blip, brief Postgres hiccup) we
  // fail OPEN to the click-through gate rather than locking the user out
  // of the app entirely. The Edge Function on the gate page will reject
  // the eventual accept call if their session is genuinely invalid, so
  // there's no security impact.
  if (isError || !isCurrentVersionAccepted(data)) {
    return (
      <Navigate
        to="/legal/eula/accept"
        state={{ from: location }}
        replace
      />
    );
  }

  return <>{children}</>;
}
